import { useState, useMemo } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useQuery } from "@tanstack/react-query";
import {
  Flex,
  Table,
  Button,
  TableRow,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  PageWrapper,
  PanelSlider,
  StretchPanel,
  TableHeadCell,
} from "@sopranodesign/pulse-components";

import { UserDetails } from "./index";

dayjs.extend(relativeTime);

const Report = ({ match: { params } }) => {
  const [showUsers, setShowUsers] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const query = useQuery({ queryKey: [`reports/${params.id}`] });
  const { data, isLoading } = query;

  // Client-side filtering and sorting
  const filteredAndSortedClients = useMemo(() => {
    if (!data?.clients) return [];

    let result = [...data.clients];

    // Filter by search text (name or email)
    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter(
        (client) =>
          client.name?.toLowerCase().includes(search) ||
          client.email?.toLowerCase().includes(search),
      );
    }

    // Sort by field
    if (sortField) {
      result.sort((a, b) => {
        let aVal, bVal;

        switch (sortField) {
          case "name":
            aVal = a.name?.toLowerCase() || "";
            bVal = b.name?.toLowerCase() || "";
            break;
          case "email":
            aVal = a.email?.toLowerCase() || "";
            bVal = b.email?.toLowerCase() || "";
            break;
          case "users_total":
            aVal = a.users?.length || 0;
            bVal = b.users?.length || 0;
            break;
          case "country":
            aVal = a.country?.toLowerCase() || "";
            bVal = b.country?.toLowerCase() || "";
            break;
          case "created_at":
            aVal = new Date(a.created_at).getTime();
            bVal = new Date(b.created_at).getTime();
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data?.clients, searchText, sortField, sortDirection]);

  const handleSort = (field) => {
    if (!field || field === null) return;

    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortableHeader = ({ label, field }) => (
    <TableHeadCell
      style={{ cursor: field ? "pointer" : "default" }}
      onClick={() => field && handleSort(field)}
    >
      {label}
      {sortField === field && (
        <span style={{ marginLeft: "4px" }}>
          {sortDirection === "asc" ? "↑" : "↓"}
        </span>
      )}
    </TableHeadCell>
  );

  return (
    <PageWrapper
      header={"Reporting"}
      actions={
        <Flex xSpace>
          <Flex style={{ width: "20rem" }} mr>
            <TextInput
              placeholder="Search by name or email"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Flex>
        </Flex>
      }
    >
      {query.isError && (
        <NoResults
          text="There was a problem loading the report"
          subtitle="Please try again later or check your connection"
        />
      )}

      {query.isSuccess &&
        filteredAndSortedClients.length === 0 &&
        !searchText && (
          <NoResults
            text="No clients found"
            subtitle="This project has no client data yet"
          />
        )}

      {query.isSuccess &&
        filteredAndSortedClients.length === 0 &&
        searchText && (
          <NoResults
            text="No matching clients found"
            subtitle="Try adjusting your search criteria"
          />
        )}

      {query.isSuccess && filteredAndSortedClients.length > 0 && (
        <StretchPanel>
          <Table loading={isLoading} loadingColumns={6}>
            <TableHead>
              <TableRow>
                <SortableHeader label="Client Name" field="name" />
                <SortableHeader label="Client Email" field="email" />
                <SortableHeader label="Number of users" field="users_total" />
                <SortableHeader label="Country" field="country" />
                <SortableHeader label="Created At" field="created_at" />
                <SortableHeader label="Actions" field={null} />
              </TableRow>
            </TableHead>
            {!isLoading && (
              <TableBody>
                {filteredAndSortedClients.map(
                  ({ country, email, name, users, created_at }, key) => (
                    <TableRow key={key}>
                      <TableCell>{name}</TableCell>
                      <TableCell>{email}</TableCell>
                      <TableCell>{users.length}</TableCell>
                      <TableCell>{country || "N/A"}</TableCell>
                      <TableCell>
                        {dayjs(created_at).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell>
                        <Flex>
                          <Button
                            disabled={users.length === 0}
                            icon="eye"
                            onClick={() => setShowUsers(users)}
                            variant="secondary"
                          >
                            Users
                          </Button>
                        </Flex>
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            )}
          </Table>
        </StretchPanel>
      )}
      {showUsers && (
        <PanelSlider header="View users" handleHide={() => setShowUsers(false)}>
          <UserDetails users={showUsers} />
        </PanelSlider>
      )}
    </PageWrapper>
  );
};

export default Report;
